import {useContext, useMemo} from 'react'
import {SiteContext} from '../contexts'
import {tryParseJSONObject} from '../../utils/utils'
export default function useOcapiSite(preloadedOcapiSite) {
    const ocapiSite = useContext(SiteContext)
    // because the ocapi is loaded through app middleware we can't set it on the appConfig,
    // so the first thing app does is call the useOcapiSite hook with the preloaded ocapiSite
    // and then we set it in the context
    if (preloadedOcapiSite && !ocapiSite.current) {
        ocapiSite.current = preloadedOcapiSite
    }
    const self = useMemo(() => {
        // create class with getters for the different ocapiSite properties
        return new (class {
            get allowedLocales() {
                return ocapiSite.current?.default?.allowedLocales
            }
            get arEnabled() {
                return ocapiSite.current?.custom?.c_result?.preferences?.arEnabled
            }
            get arModelList() {
                // TODO: cache the result
                const value = ocapiSite.current?.custom?.c_result?.preferences?.arModelList
                if (!value) return null
                try {
                    // The structure is [{description: String, pids: String[], file: String}, ...]
                    const modelList = JSON.parse(value)
                    // convert to {[pid]: {description: String, file: String}}
                    return Object.fromEntries(
                        modelList.flatMap(({pids, ...item}) => pids.map((pid) => [pid, item]))
                    )
                } catch (e) {
                    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
                        console.warn('Error parsing arModelList', e)
                    }
                    return null
                }
            }
            get brightcoveEnabled() {
                return ocapiSite.current?.custom?.c_result?.preferences?.BrightcoveEnabled
            }
            get customPreferences() {
                return ocapiSite.current?.custom?.c_result?.preferences
            }
            get sitePrefGeoLocation() {
                return tryParseJSONObject(
                    ocapiSite.current?.custom?.c_result?.preferences?.countryGeoCoordinates
                )
            }
            get hybridConfig() {
                const hybridConfig =
                    ocapiSite.current?.custom?.c_result?.preferences?.pwaHybridConfig
                if (typeof hybridConfig === 'string') {
                    try {
                        return JSON.parse(hybridConfig)
                    } catch (e) {
                        console.warn(`Error parsing hybridConfig = "${hybridConfig}"`, e)
                        return null
                    }
                }
                return hybridConfig
            }
            get myStokkeConfig() {
                const myStokkeConfig = ocapiSite.current?.custom?.c_result?.myStokkeConfig
                return Object.fromEntries(
                    myStokkeConfig?.map((config, idx) => [config.id, {...config, order: idx}]) || []
                )
            }
            get npsEnabled() {
                return Boolean(ocapiSite.current?.custom?.c_result?.preferences?.npsEnabled)
            }
            get npsIframeUrl() {
                return ocapiSite.current?.custom?.c_result?.preferences?.npsIframeUrl
            }
            get paymentMethods() {
                return ocapiSite.current?.custom?.c_result?.paymentMethods
            }
            get parcellabUserId() {
                return ocapiSite.current?.custom?.c_result?.preferences?.parcellabUserId || 1617747
            }
            get parcellabReturnOrderJS() {
                return ocapiSite.current?.custom?.c_result?.preferences?.parcellabReturnOrderJS
            }
            get parcellabReturnOrderCSS() {
                return ocapiSite.current?.custom?.c_result?.preferences?.parcellabReturnOrderCSS
            }
            get preferences() {
                return ocapiSite.current?.custom?.c_result?.preferences || {}
            }
            get reCaptchaPublicKey() {
                return ocapiSite.current?.custom?.c_result?.preferences?.reCaptchaPublicKey
            }
            get shippingMethods() {
                return ocapiSite.current?.custom?.c_result?.shippingMethods
            }
            get show3Dimages() {
                return ocapiSite.current?.custom?.c_result?.preferences?.show3Dimages
            }
            get shippingContentAssetId() {
                return ocapiSite.current?.custom?.c_result?.preferences?.shippingContentAssetId
            }
            get accountId() {
                return ocapiSite.current?.custom?.c_result?.preferences?.BrightcoveAccountID
            }
            get assemblyVideoPlayerId() {
                return ocapiSite.current?.custom?.c_result?.preferences
                    ?.BrightcoveAssemblyVideoPlayerId
            }
            get pdpContentEnabled() {
                return ocapiSite.current?.custom?.c_result?.preferences?.pwaPdpContentEnabled
            }
            get engravingImage() {
                return ocapiSite.current?.custom?.c_result?.imageUrls?.engraving
            }
            get bvApiKey() {
                return ocapiSite.current?.custom?.c_result?.preferences?.bvApiKey
            }
            get redirectLinkPartnerAllProducts() {
                return ocapiSite.current?.custom?.c_result?.preferences
                    ?.redirectLinkPartnerAllProducts
            }
            get bvEnabled() {
                return ocapiSite.current?.custom?.c_result?.preferences?.bvEnabled
            }
            get httpsCatalogContentUrl() {
                return ocapiSite.current?.custom?.c_result?.httpsCatalogContentUrl
            }
            get logo() {
                return ocapiSite?.current?.custom?.c_result?.imageUrls?.logo
            }
            get channelAdvisorURL() {
                return ocapiSite.current?.custom?.c_result?.preferences?.channelAdvisorURL
            }
            get channelAdvisorPID() {
                return ocapiSite.current?.custom?.c_result?.preferences?.channelAdvisorPID
            }
            get showAlwaysWheretobuyEnabled() {
                return ocapiSite.current?.custom?.c_result?.preferences?.showAlwaysWheretobuyEnabled
            }
            get googleVerificationTag() {
                return ocapiSite.current?.custom?.c_result?.preferences?.GoogleVerificationTag
            }
            get errorPageImage() {
                return ocapiSite.current?.custom?.c_result?.imageUrls?.errorPageImage
            }
        })()
    }, [ocapiSite])
    return self
}
